import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatusPill } from "@/components/ui-helpers";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import GoogleAccounts from "@/components/GoogleAccounts";
import SettingsExtra from "@/components/SettingsExtra";
import AccountsAdmin from "@/components/AccountsAdmin";

const CONNECTORS = [
  { id: "zoho", name: "Zoho CRM", fields: [
    { key: "client_id", label: "Client ID" },
    { key: "client_secret", label: "Client Secret", secret: true },
    { key: "refresh_token", label: "Refresh Token", secret: true },
    { key: "region", label: "Region (in/com/eu)", default: "in" },
  ]},
  { id: "meta", name: "Meta Ads", fields: [
    { key: "access_token", label: "System User Token", secret: true },
    { key: "ad_account_id", label: "Ad Account ID", default: "7748588685221971" },
  ]},
  { id: "typeform", name: "Typeform", fields: [
    { key: "token", label: "Personal Access Token", secret: true },
    { key: "webhook_secret", label: "Webhook Secret", secret: true },
  ]},
  { id: "calendly", name: "Calendly", fields: [
    { key: "token", label: "Personal Access Token", secret: true },
    { key: "webhook_signing_key", label: "Webhook Signing Key", secret: true },
  ]},
  { id: "gmail", name: "Gmail", fields: [
    { key: "client_id", label: "OAuth Client ID" },
    { key: "client_secret", label: "OAuth Client Secret", secret: true },
    { key: "refresh_token", label: "Refresh Token (khush@)", secret: true },
    { key: "refresh_token_vedant", label: "Refresh Token (vedant@)", secret: true },
  ]},
  { id: "wati", name: "Wati", fields: [
    { key: "base_url", label: "Tenant Base URL (https://live-server-NNNNN.wati.io)" },
    { key: "token", label: "API Token", secret: true },
  ]},
  { id: "zapier", name: "Zapier (monitor)", fields: [
    { key: "zaps_json", label: 'Zaps JSON [{"id":"z1","name":"Typeform→Zoho"}]' },
  ]},
  { id: "slack", name: "Slack", fields: [
    { key: "webhook_url", label: "Incoming Webhook URL", secret: true },
    { key: "bot_token", label: "Bot Token (optional)", secret: true },
    { key: "channel", label: "Default channel (e.g. #sales)" },
  ]},
];

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null); // connector id
  const [form, setForm] = useState({});
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState({});

  const load = () => api.get("/integrations").then((r) => {
    setData(r.data);
    setSettings(r.data.settings || {});
  });

  useEffect(() => { load(); }, []);

  const openEdit = (cid) => {
    const block = data.connectors[cid] || { config: {} };
    const def = CONNECTORS.find((c) => c.id === cid);
    const f = {};
    (def?.fields || []).forEach((fl) => {
      // never prefill masked secrets, user re-enters
      const cur = block.config?.[fl.key];
      if (fl.secret) f[fl.key] = "";
      else f[fl.key] = (cur && cur !== "••••") ? cur : (fl.default || "");
    });
    setForm(f);
    setEnabled(block.enabled || false);
    setOpen(cid);
  };

  const save = async () => {
    if (!isAdmin) return;
    setBusy(true);
    try {
      // strip empty strings so secrets are kept if user didn't retype
      const config = {};
      Object.entries(form).forEach(([k, v]) => { if (v !== "") config[k] = v; });
      await api.put(`/integrations/${open}`, { enabled, config });
      toast.success("Saved");
      setOpen(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const test = async (cid) => {
    if (!isAdmin) return;
    setBusy(true);
    try {
      const { data: r } = await api.post(`/integrations/${cid}/test`);
      if (r.ok) toast.success(`${cid}: OK`);
      else toast.error(`${cid}: ${r.error || r.status || "Failed"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally { setBusy(false); }
  };

  const saveSettings = async () => {
    if (!isAdmin) return;
    try {
      await api.put("/integrations/settings", {
        claude_monthly_cap_inr: Number(settings.claude_monthly_cap_inr) || 0,
        meta_campaign_include_rule: settings.meta_campaign_include_rule || "",
        meta_campaign_exclude_list: (settings.meta_campaign_exclude_list || []),
      });
      toast.success("Settings saved");
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  if (!data) return <div className="p-6 text-xs text-neutral-500">Loading…</div>;

  return (
    <div data-testid="settings-page">
      <PageHeader
        title="Settings"
        sub={isAdmin ? "Integrations · guardrails · operator settings" : "View only · operators cannot edit integrations"}
      />
      <div className="p-4 md:p-6 space-y-6">
        {isAdmin && <AccountsAdmin />}
        <GoogleAccounts isAdmin={isAdmin} />
        <SettingsExtra isAdmin={isAdmin} />
        <section>
          <h2 className="text-sm font-medium mb-3">Integrations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="integrations-grid">
            {CONNECTORS.map((c) => {
              const block = data.connectors[c.id] || {};
              return (
                <div key={c.id} className="panel p-4" data-testid={`conn-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-sm font-medium">{c.name}</div>
                      <div className="text-[11px] text-neutral-500 mt-0.5">
                        {block.last_sync ? `Last sync ${new Date(block.last_sync).toLocaleString("en-IN")}` : "Never synced"}
                      </div>
                      {block.last_error && (
                        <div className="text-[11px] text-[var(--breach)] mt-1 line-clamp-2">{String(block.last_error)}</div>
                      )}
                    </div>
                    <StatusPill status={block.status || "not_configured"} />
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      className="btn"
                      onClick={() => openEdit(c.id)}
                      disabled={!isAdmin}
                      data-testid={`conn-${c.id}-edit`}
                    >Edit</button>
                    <button
                      className="btn"
                      onClick={() => test(c.id)}
                      disabled={!isAdmin || busy}
                      data-testid={`conn-${c.id}-test`}
                    >Test</button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="panel p-4" data-testid="general-settings">
          <h2 className="text-sm font-medium mb-3">General</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-[11px] text-neutral-500 block mb-1">Claude monthly cap (₹)</label>
              <input
                type="number"
                className="w-full px-3 py-2 rounded border text-sm"
                value={settings.claude_monthly_cap_inr ?? ""}
                onChange={(e) => setSettings({ ...settings, claude_monthly_cap_inr: e.target.value })}
                disabled={!isAdmin}
                data-testid="claude-cap-input"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] text-neutral-500 block mb-1">Meta campaign include rule (regex / substring)</label>
              <input
                className="w-full px-3 py-2 rounded border text-sm"
                value={settings.meta_campaign_include_rule || ""}
                onChange={(e) => setSettings({ ...settings, meta_campaign_include_rule: e.target.value })}
                disabled={!isAdmin}
                placeholder="e.g. ^T&T_"
                data-testid="meta-include-input"
              />
              <div className="text-[10px] text-neutral-600 mt-1">Only include T&T lead-gen campaigns. Excludes client campaigns in the same ad account.</div>
            </div>
          </div>
          {isAdmin && (
            <div className="mt-3">
              <button className="btn btn-primary" onClick={saveSettings} data-testid="save-settings-btn">Save settings</button>
            </div>
          )}
        </section>
      </div>

      {/* Edit sheet */}
      {open && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end md:items-center justify-center p-4" onClick={() => setOpen(null)}>
          <div className="panel p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="connector-sheet">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-sm font-medium">{CONNECTORS.find((c) => c.id === open)?.name}</div>
                <div className="text-[11px] text-neutral-500">Credentials stored server-side. Secrets masked after save.</div>
              </div>
              <button onClick={() => setOpen(null)} className="btn-ghost">✕</button>
            </div>
            <div className="space-y-3">
              {CONNECTORS.find((c) => c.id === open)?.fields.map((f) => (
                <div key={f.key}>
                  <label className="text-[11px] text-neutral-500 block mb-1">
                    {f.label} {f.secret && <span className="text-[10px] text-neutral-600">· leave empty to keep current</span>}
                  </label>
                  <input
                    type={f.secret ? "password" : "text"}
                    className="w-full px-3 py-2 rounded border text-sm font-mono"
                    value={form[f.key] || ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    data-testid={`field-${open}-${f.key}`}
                  />
                </div>
              ))}
              <label className="flex items-center gap-2 text-xs mt-3">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  data-testid="connector-enabled"
                />
                <span>Enable this connector</span>
              </label>
            </div>
            <div className="flex items-center gap-2 mt-5">
              <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="connector-save">
                {busy ? "Saving…" : "Save"}
              </button>
              <button className="btn" onClick={() => setOpen(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
